'use client';

import { useEffect, useSyncExternalStore } from 'react';
import {
  getConnectionState,
  getShouldFallback,
  getSocket,
  subscribeConnection,
  type EchoConnectionState
} from '@/lib/echo';

function getServerSnapshot(): EchoConnectionState {
  return 'idle';
}

function getServerFallback(): boolean {
  return false;
}

/**
 * Tracks the Reverb/Pusher connection state and reports whether consumers
 * should fall back to polling.
 *
 * `shouldFallback` flips on `failed` immediately, and after a grace window
 * (managed in `lib/echo.ts`) for transient `unavailable`/`disconnected`
 * states so Pusher's auto-retry has a chance to recover before we poll.
 */
export function useEchoConnection(): {
  state: EchoConnectionState;
  shouldFallback: boolean;
} {
  const state = useSyncExternalStore(
    subscribeConnection,
    getConnectionState,
    getServerSnapshot
  );
  const shouldFallback = useSyncExternalStore(
    subscribeConnection,
    getShouldFallback,
    getServerFallback
  );

  // Trigger socket initialization on first mount so the connection state
  // machine starts emitting events. If construction fails, echo.ts marks
  // the state as `failed` so consumers fall back.
  useEffect(() => {
    void getSocket().catch(() => {});
  }, []);

  return { state, shouldFallback };
}
